import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from '@posthog/react';
import * as Sentry from '@sentry/react';
import './index.css';
import { App } from './App';

// Crash reporting — only in production, only when a DSN is configured.
// PII is stripped so player emails/usernames never leave the device.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (import.meta.env.PROD && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

// The embed is served from a folder (itch.io: /html/<id>/index.html) and the
// app writes its views into the address (/profile, /game). Asset paths are
// relative in that build, so after the first view change ./badges/... would
// resolve against /profile and 404. A <base> pinned to the folder the page
// loaded from keeps every relative path pointing at the build.
if (import.meta.env.VITE_EMBED === '1') {
  const base = document.createElement('base');
  base.href = new URL('./', window.location.href).href;
  document.head.prepend(base);
}

// The embed cannot own a scope on someone else's origin, so it does not ask.
if ('serviceWorker' in navigator && import.meta.env.VITE_EMBED !== '1') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.log('ServiceWorker registration failed: ', err);
    });
  });
}

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

const posthogOptions = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: true,
  // Crash reporting with zero extra config: uncaught errors and unhandled
  // rejections land in PostHog Error Tracking under the same project key.
  capture_exceptions: true,
  persistence: 'localStorage' as const,
};

// Without a key, PostHogProvider still mounts and initializes an untokened
// client: it captures nothing and logs a misconfiguration error on every load.
// Mount the provider only when a key is present so a missing/uninjected build
// env var is silent for players and obvious in the build log instead.
if (!posthogKey) {
  console.warn('Analytics disabled: VITE_POSTHOG_KEY is not set in this build.');
}

const app = (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {posthogKey ? (
      <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
        {app}
      </PostHogProvider>
    ) : app}
  </StrictMode>,
);
